<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-200">
        <div>
          <h3 class="text-xl font-bold text-gray-900">Tenure Rules</h3>
          <p class="text-sm text-gray-600">{{ bank.name }}</p>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <X class="w-6 h-6" />
        </button>
      </div>

      <!-- Form -->
      <div class="p-6 space-y-4">
        <p class="text-sm text-gray-600">
          Define how many days are allocated based on employee tenure (years of service).
        </p>

        <!-- Rules List -->
        <div class="space-y-3">
          <div
            v-for="(rule, index) in rules"
            :key="index"
            class="flex items-end gap-3 p-4 bg-gray-50 rounded-lg"
          >
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Minimum Years
              </label>
              <input
                v-model.number="rule.min_years"
                type="number"
                step="0.1"
                min="0"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>

            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Maximum Years
                <span class="text-xs text-gray-500">(optional, blank = no limit)</span>
              </label>
              <input
                v-model.number="rule.max_years"
                type="number"
                step="0.1"
                min="0"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 5"
              />
            </div>

            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Allocated Days *
              </label>
              <input
                v-model.number="rule.allocated_days"
                type="number"
                step="0.5"
                min="0"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 14"
              />
            </div>

            <button
              @click="removeRule(index)"
              v-if="rules.length > 1"
              class="p-2 text-red-600 hover:bg-red-50 rounded-lg"
              title="Remove Rule"
            >
              <Trash2 class="w-5 h-5" />
            </button>
          </div>
        </div>

        <!-- Add Rule Button -->
        <button
          @click="addRule"
          class="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 flex items-center justify-center gap-2"
        >
          <Plus class="w-4 h-4" />
          Add Tenure Rule
        </button>

        <!-- Example -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p class="text-sm font-medium text-blue-900 mb-2">Example:</p>
          <ul class="text-sm text-blue-800 space-y-1">
            <li>• 0-5 years = 14 days</li>
            <li>• 5+ years = 21 days</li>
          </ul>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {{ error }}
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="submitting"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ submitting ? 'Saving...' : 'Save Rules' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { X, Plus, Trash2 } from 'lucide-vue-next';
import balanceBankService from '@/services/balanceBankService';

const props = defineProps({
  bank: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const submitting = ref(false);
const error = ref(null);
const rules = ref([
  { min_years: 0, max_years: 5, allocated_days: 14 },
  { min_years: 5, max_years: null, allocated_days: 21 }
]);

onMounted(async () => {
  loading.value = true;
  try {
    const response = await balanceBankService.getTenureRules(props.bank.id);
    if (response.data && response.data.length > 0) {
      rules.value = response.data.map(r => ({
        min_years: parseFloat(r.min_years),
        max_years: r.max_years ? parseFloat(r.max_years) : null,
        allocated_days: parseFloat(r.allocated_days)
      }));
    }
  } catch (err) {
    console.error('Error loading tenure rules:', err);
  } finally {
    loading.value = false;
  }
});

function addRule() {
  rules.value.push({
    min_years: 0,
    max_years: null,
    allocated_days: 0
  });
}

function removeRule(index) {
  rules.value.splice(index, 1);
}

async function handleSave() {
  submitting.value = true;
  error.value = null;

  try {
    await balanceBankService.updateTenureRules(props.bank.id, rules.value);
    emit('saved');
    emit('close');
  } catch (err) {
    console.error('Error saving tenure rules:', err);
    error.value = err.response?.data?.message || 'Failed to save tenure rules';
  } finally {
    submitting.value = false;
  }
}
</script>
